<template>
  <footer class="qp-footer">
    <div class="qp-footer__inner">
      <!-- Brand -->
      <div class="qp-footer__brand">
        <router-link to="/" class="qp-footer__logo" aria-label="QuickPrice">
          <span class="qp-footer__mark" aria-hidden="true"></span>
          <span class="qp-footer__wordmark">QuickPrice</span>
        </router-link>
        <p class="qp-footer__copy">&copy; 2026 {{ $t('footer.copyright') }}</p>
        <p class="qp-footer__tagline">{{ $t('footer.powered_by') }}</p>
      </div>

      <!-- Pages -->
      <nav class="qp-footer__column" aria-labelledby="footer-pages">
        <h3 id="footer-pages" class="qp-footer__title">{{ $t('footer.pages') }}</h3>
        <ul class="qp-footer__list">
          <li><router-link to="/" class="qp-footer__link">{{ $t('navbar.home') }}</router-link></li>
          <li><router-link to="/about" class="qp-footer__link">{{ $t('navbar.about') }}</router-link></li>
          <li><router-link to="/smart_search" class="qp-footer__link">{{ $t('navbar.product_info') }}</router-link></li>
          <li><router-link to="/pricing" class="qp-footer__link">{{ $t('navbar.pricing') }}</router-link></li>
        </ul>
      </nav>

      <!-- Contacts -->
      <div class="qp-footer__column">
        <h3 class="qp-footer__title">{{ $t('footer.contacts') }}</h3>
        <ul class="qp-footer__list">
          <li><a href="tel:+77022736072" class="qp-footer__link qp-footer__link--mono">+7 702 273 60 72</a></li>
        </ul>
      </div>

      <!-- Social -->
      <div class="qp-footer__column">
        <h3 class="qp-footer__title">{{ $t('footer.connect_with_us') }}</h3>
        <div class="qp-footer__social">
          <a :href="wpLink" target="_blank" rel="noopener" class="qp-footer__social-link" :title="$t('footer.whatsapp')">
            <font-awesome-icon :icon="['fab', 'whatsapp']"/>
            <span>WhatsApp</span>
          </a>
          <!-- <a :href="tgLink" target="_blank" rel="noopener" class="qp-footer__social-link" :title="$t('footer.telegram')">
            <font-awesome-icon :icon="['fab', 'telegram']"/>
            <span>Telegram</span>
          </a> -->
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { useTheme } from '@/composables/useTheme';

export default {
  name: 'BasicFooter',
  setup() {
    return useTheme();
  },
  computed: {
    yearRange() {
      const startYear = 2024;
      const currentYear = new Date().getFullYear();
      return currentYear > startYear ? `${startYear}–${currentYear}` : startYear.toString();
    },
    // tgLink() { return 'https://t.me/somehowsher'; },
    wpLink() { return 'https://wa.me/77022736072'; },
  },
};
</script>

<style scoped>
.qp-footer {
  width: 100%;
  padding: var(--space-2xl) var(--space-lg) var(--space-xl);
  margin-top: var(--space-2xl);
  border-top: 1px solid var(--hairline);
  background: var(--surface-canvas);
  color: var(--ink-muted);
  font-size: var(--text-xs);
}

.qp-footer__inner {
  display: grid;
  grid-template-columns: 1.4fr repeat(3, 1fr);
  gap: var(--space-xl);
  max-width: var(--layout-shell-max);
  margin-inline: auto;
  align-items: flex-start;
}

.qp-footer__brand {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}

.qp-footer__logo {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--ink);
  text-decoration: none;
  line-height: 1;
  width: max-content;
  transition: opacity var(--dur-xs) var(--ease-out-quart);
}

.qp-footer__logo:hover { opacity: 0.85; }

.qp-footer__mark {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: var(--accent);
  transform: rotate(45deg);
  transition: transform var(--dur-md) var(--ease-out-expo);
}

.qp-footer__logo:hover .qp-footer__mark { transform: rotate(135deg); }

.qp-footer__wordmark {
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-tight);
}

.qp-footer__copy {
  margin: var(--space-xs) 0 0;
  font-size: var(--text-xs);
  color: var(--ink-muted);
}

.qp-footer__tagline {
  margin: 0;
  max-width: 32ch;
  font-size: var(--text-2xs);
  color: var(--ink-faint);
  line-height: var(--leading-normal);
}

.qp-footer__column {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.qp-footer__title {
  margin: 0 0 var(--space-2xs);
  font-size: var(--text-2xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--ink-faint);
}

.qp-footer__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);
}

.qp-footer__link {
  display: inline-block;
  font-size: var(--text-xs);
  color: var(--ink-muted);
  text-decoration: none;
  transition: color var(--dur-xs) var(--ease-out-quart),
              padding var(--dur-xs) var(--ease-out-quart);
}

.qp-footer__link:hover {
  color: var(--accent);
  padding-inline-start: 2px;
}

.qp-footer__link--mono {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.qp-footer__social {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);
}

.qp-footer__social-link {
  display: inline-flex;
  align-items: center;
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  color: var(--ink-muted);
  gap: var(--space-2xs);
  text-decoration: none;
  padding: 4px 0;
  position: relative;
  width: max-content;
  transition: color var(--dur-xs) var(--ease-out-quart);
}

.qp-footer__social-link::after {
  content: '↗';
  margin-inline-start: 6px;
  font-family: var(--font-mono);
  font-size: var(--text-2xs);
  opacity: 0.6;
  transition: transform var(--dur-sm) var(--ease-out-expo);
}

.qp-footer__social-link:hover {
  color: var(--accent);
}

.qp-footer__social-link:hover::after {
  transform: translate(2px, -2px);
}

/* Responsive */

@media (max-width: 960px) {
  .qp-footer { padding: var(--space-xl) var(--space-md) calc(var(--space-xl) + 72px); }
  .qp-footer__inner { grid-template-columns: 1fr 1fr; gap: var(--space-lg); }
  .qp-footer__brand { grid-column: 1 / -1; }
}

@media (max-width: 560px) {
  .qp-footer__inner { grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce) {
  .qp-footer__mark,
  .qp-footer__social-link::after,
  .qp-footer__link { transition: none; }
}
</style>
